import { useCallback, useEffect, useRef, useState } from "react";

import { useWalletStore } from "@/features/wallet/stores/walletStore";
import { walletService } from "@/features/wallet/services/wallet";

import { isAppleWebView } from "utilities/display";
import { getBridge } from "utilities/bridge";
import { message } from "utilities/message/message";
import { parseError } from "utilities/errorsParser";
import { useHasPermission } from "@/features/auth/hooks/usePermission";

const SESSION_START_ERROR =
  "We couldn't start Apple Pay. Please try again, or choose another payment method.";

type ApplePayHandlers = {
  onSuccess: () => void;
  onDismiss?: () => void;
};

export const useIsApplePayAvailable = (): boolean => {
  const [isAvailable, setIsAvailable] = useState(false);
  const appApplePayAvailable = useWalletStore(
    (state) => state.appApplePayAvailable,
  );

  const canDepositApplePay = useHasPermission("deposit_ApplePay");

  useEffect(() => {
    if (
      typeof window === "undefined" ||
      !canDepositApplePay ||
      process.env.GATSBY_ENABLE_APPLE_PAY === "false"
    ) {
      setIsAvailable(false);
      return;
    }

    try {
      setIsAvailable(
        !!(window.ApplePaySession && ApplePaySession.canMakePayments()),
      );
    } catch {
      setIsAvailable(false);
    }
  }, [appApplePayAvailable, canDepositApplePay]);

  return isAvailable;
};

export const useApplePay = (): ((
  amount: number,
  handlers: ApplePayHandlers,
) => void) => {
  const hasCurrentApplePaySession = useRef(false);
  const isAppleApp = isAppleWebView();

  return useCallback(
    (amount: number, { onSuccess, onDismiss }: ApplePayHandlers) => {
      const store = useWalletStore.getState();

      if (hasCurrentApplePaySession.current) {
        console.warn("Apple Pay session already active");
        return;
      }

      // a retry should not sit under the last attempt's failure
      store.setAppleDepositError(null);

      if (isAppleApp) {
        // use native payments api if we are within iOS app
        getBridge().send.applePayStart({ amount });
        return;
      }

      const session = new ApplePaySession(1, {
        countryCode: "AU",
        currencyCode: "AUD",
        supportedNetworks: ["visa", "masterCard"],
        //supportsDebit is to disable credit cards
        //see: https://developer.apple.com/documentation/apple_pay_on_the_web/applepaymerchantcapability
        merchantCapabilities: ["supports3DS", "supportsDebit"],
        total: {
          label: "Picklebet",
          type: "final",
          amount: `${amount}`,
        },
      });

      // every path that leaves the sheet closed without a deposit runs through
      // here, so callers can release UI they locked for the session
      const dismiss = () => {
        hasCurrentApplePaySession.current = false;
        onDismiss?.();
      };

      session.onvalidatemerchant = async (
        event: ApplePayJS.ApplePayValidateMerchantEvent,
      ) => {
        const [response, error] = await message("generate-apple-pay-session", {
          validationUrl: event.validationURL,
        });

        if (error || !response?.data?.merchantSession) {
          console.warn("Failed to initialise Apple Pay session.", error);
          session.abort();
          store.setAppleDepositError(SESSION_START_ERROR);
          dismiss();
          return;
        }

        try {
          const merchantSession = JSON.parse(response.data.merchantSession);

          // complete session verification
          session.completeMerchantValidation(merchantSession);
        } catch (parseError) {
          // this should technically not happen, but if it does, we should log it
          console.warn("Failed to parse Apple Pay session.", parseError);
          session.abort();
          store.setAppleDepositError(SESSION_START_ERROR);
          dismiss();
        }
      };

      session.onpaymentauthorized = async (
        event: ApplePayJS.ApplePayPaymentAuthorizedEvent,
      ) => {
        store.setAppleDepositError(null);
        store.setAppleDepositStatus("processing");

        const outcome = await walletService.merchantWarriorDeposit({
          digitalWalletToken: JSON.stringify(event.payment.token.paymentData),
          amount,
          digitalWalletType: "Apple",
          panSuffix:
            event.payment.token.paymentMethod.displayName?.slice(-4) ?? "", // apple returns "Network 1234" eg: "Visa 0492", but we just need the last 4 characters
        });

        const isSuccess = outcome.ok && outcome.data === "success";

        if (isSuccess) {
          store.setAppleDepositStatus("success");
          await onSuccess();
        } else {
          if (!outcome.ok) {
            store.setAppleDepositError(parseError(outcome.error));
          }
          store.setAppleDepositStatus("ready");
        }

        session.completePayment({
          status: isSuccess
            ? ApplePaySession.STATUS_SUCCESS
            : ApplePaySession.STATUS_FAILURE,
        });

        if (isSuccess) {
          hasCurrentApplePaySession.current = false;
        } else {
          dismiss();
        }
      };

      session.oncancel = dismiss;

      try {
        session.begin();
      } catch (error) {
        // the SDK throws (a bare string) when a session is already in flight or
        // the merchant isn't configured. Nothing else reports back in that
        // case, so the caller would be left holding its UI shut forever
        console.warn("Failed to begin Apple Pay session.", error);
        useWalletStore.getState().setAppleDepositError(SESSION_START_ERROR);
        dismiss();
        return;
      }

      hasCurrentApplePaySession.current = true;
    },
    [],
  );
};
